import React, { useState, useEffect } from "react";
import { Loader, Users, MapPin, Award } from "lucide-react";
import { Helmet } from "react-helmet-async";
import seoData from "../../../assets/data/seo.json";
import { get_all_members } from "../../../hooks/Apis";




const OurMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await fetch(get_all_members);

      if (!response.ok) throw new Error("Failed to fetch members");
      const data = await response.json();
      if (data.status === "success") {
        setMembers(data.members);
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      setError("Failed to load members. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600 font-medium animate-pulse">Loading Our Members...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="bg-red-50 p-6 rounded-2xl border border-red-100 max-w-md">
          <p className="text-red-600 font-semibold mb-2">Error</p>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={fetchMembers}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const seo = seoData.our_members || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content={seo.ogImage} />
        <meta property="og:url" content={seo.canonical} />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.ogImage} />
      </Helmet>

      {/* Hero Section */}

      <div className="bg-blue-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
            Our <span className="text-yellow-400">Members</span>
          </h1>
          <p className="text-blue-100 text-lg md:text-xl max-w-2xl mx-auto font-light">
            Dedicated individuals working together to uphold consumer rights and social justice across Bharat.
          </p>
          <div className="mt-8 flex justify-center items-center gap-2 text-blue-200">
            <Users className="w-5 h-5" />
            <span className="font-semibold">{members.length} Registered Members</span>
          </div>
        </div>
      </div>

      {/* Members Grid */}
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">

          {members.map((member, index) => (
            <div 
              key={index}
              className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-gray-100 flex flex-col text-center"
            >
              <div className="mb-1">
                {member.id_card_number ? (
                  <div className="text-[8px] sm:text-[10px] text-blue-600 font-bold font-mono mb-1">
                    {member.id_card_number}
                  </div>
                ) : (
                   <div className="text-[8px] sm:text-[10px] text-gray-400 font-mono mb-1 italic">
                    NO ID
                  </div>
                )}
                <h3 className="text-[10px] sm:text-xs font-bold text-gray-900 leading-tight mb-1 line-clamp-2 uppercase">
                  {member.name}
                </h3>
                <div className="inline-flex items-center justify-center px-2 py-0.5 bg-gray-50 text-gray-600 rounded-md text-[8px] font-medium uppercase tracking-tighter border border-gray-200">
                  {member.membership_type}
                </div>
              </div>
            </div>
          ))}
        </div>


        {members.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800">No members found</h3>
            <p className="text-gray-500">Registered members will appear here soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OurMembers;
